import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { resolveUploadUrl } from '../../utils/mediaUrl';

const CMSClientLogos = () => {
  const [logos, setLogos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingLogo, setEditingLogo] = useState(null);
  const [form, setForm] = useState({
    name: '',
    website_url: '',
    display_order: 0,
    is_active: true,
  });
  const [logoFile, setLogoFile] = useState(null);

  useEffect(() => {
    loadLogos();
  }, []);

  const loadLogos = async () => {
    try {
      setLoading(true);
      const res = await api.getClientLogos();
      if (res.success) setLogos(res.data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingLogo(null);
    setForm({
      name: '',
      website_url: '',
      display_order: logos.length,
      is_active: true,
    });
    setLogoFile(null);
    setShowModal(true);
  };

  const handleOpenEdit = (logo) => {
    setEditingLogo(logo);
    setForm({
      name: logo.name || '',
      website_url: logo.website_url || '',
      display_order: logo.display_order ?? 0,
      is_active: Boolean(logo.is_active),
    });
    setLogoFile(null);
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files && e.target.files[0];
    setLogoFile(file || null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!editingLogo && !logoFile) {
      setError('Logo image is required');
      return;
    }
    try {
      if (editingLogo) {
        await api.updateClientLogo(editingLogo.id, form, logoFile);
      } else {
        await api.createClientLogo(form, logoFile);
      }
      setShowModal(false);
      loadLogos();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this client logo?')) return;
    try {
      await api.deleteClientLogo(id);
      loadLogos();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSeedDefault = async () => {
    setError('');
    try {
      const res = await api.seedDefaultClientLogos();
      if (res.message) window.alert(res.message);
      loadLogos();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <>
      {error && (
        <div className="alert alert-danger alert-dismissible">
          <button type="button" className="close" onClick={() => setError('')}>&times;</button>
          {error}
        </div>
      )}

      <div className="row mb-3">
        <div className="col-md-12">
          <button type="button" className="btn btn-primary btn-sm mr-2" onClick={handleOpenCreate}>
            <i className="fas fa-plus"></i> New Client Logo
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleSeedDefault}>
            <i className="fas fa-magic"></i> Seed Default Logos
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Client Logos</h3>
          <p className="text-muted mb-0 small">
            Logos shown in the home page &quot;Trusted by industry leaders&quot; carousel. Use PNG or SVG on white background for best results.
          </p>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center p-4">Loading...</div>
          ) : (
            <table className="table table-bordered table-hover mb-0">
              <thead>
                <tr>
                  <th style={{ width: 120 }}>Logo</th>
                  <th>Client name</th>
                  <th>Website</th>
                  <th style={{ width: 80 }}>Order</th>
                  <th style={{ width: 90 }}>Status</th>
                  <th style={{ width: 120 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {logos.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center">No client logos yet</td>
                  </tr>
                ) : (
                  logos.map((logo) => (
                    <tr key={logo.id}>
                      <td className="align-middle text-center">
                        {logo.logo_url ? (
                          <img
                            src={resolveUploadUrl(logo.logo_url)}
                            alt=""
                            style={{
                              width: 100,
                              height: 48,
                              objectFit: 'contain',
                              background: '#fff',
                              borderRadius: 4,
                              padding: 4,
                            }}
                          />
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="align-middle">{logo.name}</td>
                      <td className="align-middle">
                        {logo.website_url ? (
                          <a href={logo.website_url} target="_blank" rel="noopener noreferrer">
                            {logo.website_url}
                          </a>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="align-middle text-center">{logo.display_order ?? 0}</td>
                      <td className="align-middle">
                        <span className={`badge ${logo.is_active ? 'bg-success' : 'bg-secondary'}`}>
                          {logo.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="align-middle">
                        <button
                          type="button"
                          className="btn btn-sm btn-info mr-1"
                          onClick={() => handleOpenEdit(logo)}
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(logo.id)}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1040 }}
            onClick={() => setShowModal(false)}
          ></div>
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            style={{ zIndex: 1050 }}
          >
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h4 className="modal-title">{editingLogo ? 'Edit Client Logo' : 'New Client Logo'}</h4>
                  <button type="button" className="close" onClick={() => setShowModal(false)}>
                    <span>&times;</span>
                  </button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    <div className="form-group">
                      <label>Client name *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        required
                        placeholder="Isuzu East Africa"
                      />
                      <small className="form-text text-muted">Used as image alt text on the home page</small>
                    </div>
                    <div className="form-group">
                      <label>Logo image {!editingLogo && '*'}</label>
                      <input
                        type="file"
                        accept="image/*"
                        className="form-control-file"
                        onChange={handleLogoChange}
                        required={!editingLogo}
                      />
                      {editingLogo && editingLogo.logo_url && !logoFile && (
                        <div className="mt-2">
                          <img
                            src={resolveUploadUrl(editingLogo.logo_url)}
                            alt=""
                            style={{
                              maxWidth: '100%',
                              maxHeight: 80,
                              objectFit: 'contain',
                              background: '#fff',
                              padding: 8,
                              borderRadius: 4,
                            }}
                          />
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Website URL (optional)</label>
                      <input
                        type="url"
                        className="form-control"
                        name="website_url"
                        value={form.website_url}
                        onChange={handleChange}
                        placeholder="https://example.com"
                      />
                    </div>
                    <div className="form-group">
                      <label>Display order</label>
                      <input
                        type="number"
                        className="form-control"
                        name="display_order"
                        value={form.display_order}
                        onChange={handleChange}
                        min="0"
                      />
                    </div>
                    <div className="form-group mb-0">
                      <div className="custom-control custom-checkbox">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id="client_logo_active"
                          name="is_active"
                          checked={form.is_active}
                          onChange={handleChange}
                        />
                        <label className="custom-control-label" htmlFor="client_logo_active">
                          Active (visible on home page)
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-default btn-sm" onClick={() => setShowModal(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary btn-sm">
                      {editingLogo ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default CMSClientLogos;
